import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'
import {
  Button,
  Paragraph,
  CheckIcon,
  UpgradeDialog,
  List,
  Image,
} from '@buffer-mono/popcorn'

import { trackUpgradePathViewed } from '../../../../common/hooks/useSegmentTracking'
import { useUser } from '../../../../common/context/User'
import { MODALS } from '../../../../common/types'
import useDismissObject from '../../../../common/hooks/useDismissObject'
import { addMonths } from 'date-fns'

import styles from './TrialExpired.module.css'
import trackTrialExpiredModalViewed from '../../../../tracking/TrialPaths/trackTrialExpiredModalViewed'
import trackTrialExpiredModalMainCTAClicked from '../../../../tracking/TrialPaths/trackTrialExpiredModalMainCTAClicked'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'

const benefitCheckPoints = [
  'Schedule up to 2,000 posts',
  'Track audience growth with Insights',
  'Engage with your audience directly',
  'Collaborate with unlimited users',
  'Save & manage Hashtags easily',
  'Get unlimited tags',
]

interface TrialExpiredProps {
  open?: boolean
  onOpenChange: (open: boolean) => void
}

export const TrialExpired = ({
  open = true,
  onOpenChange,
}: TrialExpiredProps): JSX.Element => {
  const user = useUser()
  const { modalData, openModal } = useModalManager()
  const { onDismissObject } = useDismissObject({
    organizationId: user?.currentOrganization?.id,
    dismissedObject: {
      id: 'trial_expired_modal',
      expiresAt: addMonths(new Date(), 6).toISOString(),
    },
  })

  const { ctaButton, upgradePathName, ctaView, ctaLocation } = modalData || {}

  const defaultUpgradePathName = 'trialExpiredModal-upgrade'
  const payload = useMemo(
    () => ({
      ctaButton: ctaButton || 'upgradeOptions',
      upgradePathName: upgradePathName || defaultUpgradePathName,
      ctaView: ctaView || 'trialExpiredModal',
      ctaLocation: ctaLocation || 'trialExpiredModal',
    }),
    [ctaButton, ctaLocation, ctaView, upgradePathName],
  )

  const onUpgrade = (): void => {
    openModal({
      key: MODALS.planSelector,
      data: {
        cta: 'navBar-trialExpiredModal-trialExpired-1',
        upgradePathName: 'trialExpired-upgrade',
      },
    })
  }

  useEffect(() => {
    trackTrialExpiredModalViewed({
      payload,
      user,
    })
    trackUpgradePathViewed({
      payload: {
        upgradePathName: defaultUpgradePathName,
        cta: 'navBar-trialExpiredModal-trialExpired-1',
      },
      user,
    })
  }, [payload, user])

  const handleMainCTAClicked = (): void => {
    trackTrialExpiredModalMainCTAClicked({
      payload,
      user,
    })
    onDismissObject()
    onUpgrade()
  }

  const handleStayOnFree = (): void => {
    onDismissObject()
    onOpenChange(false)
  }

  const description = `Your free trial has expired and you are back on the limited free plan. Upgrade now to keep enjoying all these benefits and more...`

  return (
    <UpgradeDialog open={open} onOpenChange={onOpenChange}>
      <UpgradeDialog.Content size="large">
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>
            Your trial period is over. What&apos;s next?
          </UpgradeDialog.Title>
          <Paragraph>{description}</Paragraph>
          <List>
            {benefitCheckPoints.map(
              (detail): JSX.Element => (
                <List.Item
                  key={detail}
                  className={styles.benefitItem}
                  icon={CheckIcon}
                >
                  {detail}
                </List.Item>
              ),
            )}
          </List>
          <UpgradeDialog.Actions>
            <Button variant="primary" onClick={handleMainCTAClicked}>
              See Upgrade Options
            </Button>
            <Button variant="secondary" onClick={handleStayOnFree}>
              Stay on the Free Plan
            </Button>
          </UpgradeDialog.Actions>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-ui.s3.amazonaws.com/trial-expired/trial-over_x2.png"
            alt="Social proof illustration"
            placeholder="https://buffer-ui.s3.amazonaws.com/trial-expired/trial-over.png"
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}

export default TrialExpired
